// Copy and routing data for the benefits-calculator exploration. Illustrative
// language — the point is the tone and the shape of the exchange, not final
// wording. Product copy here follows the house style: plain short sentences,
// no em dashes.
//
// One rule holds across the whole file: no count and no monetary figure is
// written into the copy. The frames compute every number from the ported
// screener and engine, so a claim on the page cannot survive the engine
// disagreeing with it. That rule is what caught the payoff frame asserting a
// win the engine did not produce.

import type { Answers } from '@/benefits-flow/screener/questions';

// The same person as the divergence exploration, deliberately: the two pages
// are read one after the other, and a second name reads like a second family.
export const PERSON = {
  name: 'Margaret',
};

// ── A. How each question could be answered ───────────────────────────────────

// How an answer is meant to arrive AT FIRST CONTACT. That qualifier is the whole
// point: this is the entry point to the app, so there is no care profile yet and
// nothing is already known. Every answer below has to come from the person in
// front of us, one way or another. "We already hold it" is not a channel here —
// it is what the second visit looks like, and it is argued in section C.
//
// Modality routing at question granularity: the cheapest and safest channel
// differs per question, so not every question should offer every mode (a point
// from the 5 Aug session).
//
// `tap` is deliberately rare. It is not the default and it is not a fallback
// listed against every row — being asked outright is the floor under all of this,
// stated once, not a channel we design for. It appears below only where inferring
// the answer would be wrong AND the person can give it immediately, which comes
// to two questions out of nineteen.
export type AnswerRoute = 'waffle' | 'document' | 'typed' | 'tap';

// No tone per channel. These are categories with no ordering, so they all render
// in one outlined treatment and the label carries the meaning. Colour on this
// page is reserved for the reliability scale, which is the one worth scanning.
export const ROUTE_META: Record<AnswerRoute, { label: string; short: string }> =
  {
    waffle: { label: 'Falls out of the conversation', short: 'Talk' },
    document: { label: 'Lift it off a document', short: 'Document' },
    typed: { label: 'They have to look it up', short: 'Look up' },
    tap: { label: 'Has to be asked outright', short: 'Ask' },
  };

// Per-question routing. `routes[0]` is the channel we lead with; anything after
// it is a genuine second option, not a fallback. Being asked outright is the
// floor under every row and is deliberately not listed as an option — see
// ROUTING.floor.
export const ROUTES: Record<string, { routes: AnswerRoute[]; note?: string }> =
  {
    'care-recipient-location': {
      routes: ['waffle', 'document'],
    },
    'care-recipient-date-of-birth': {
      routes: ['waffle'],
    },
    'care-recipient-needs-help-time-of-day': {
      routes: ['waffle'],
    },
    'care-recipient-is-disabled': {
      routes: ['waffle'],
    },
    'care-recipient-condition-duration': {
      routes: ['waffle'],
    },
    'care-recipient-condition-expected': {
      routes: ['waffle'],
    },
    'care-recipient-disability-type': {
      routes: ['waffle', 'document'],
    },
    'care-recipient-terminally-ill': {
      routes: ['tap'],
    },
    'care-recipient-home-requires-adaptations': {
      routes: ['waffle'],
    },
    'care-recipient-in-nursing-home': {
      routes: ['waffle', 'document'],
    },
    'care-recipient-needs-personal-care': {
      routes: ['waffle'],
    },
    'care-recipient-needs-nursing-care': {
      routes: ['tap'],
    },
    'proto-existing-awards': {
      routes: ['document', 'waffle'],
    },
    'proto-pip-parts': {
      routes: ['document', 'waffle'],
    },
    'proto-dla-parts': {
      routes: ['document', 'waffle'],
    },
    'proto-adp-parts': {
      routes: ['document', 'waffle'],
    },
    'proto-carer-present': {
      routes: ['waffle'],
    },
    'proto-carer-hours-35-plus': {
      routes: ['waffle'],
    },
    'proto-carer-earnings-below-204': {
      routes: ['typed', 'document'],
    },
    'care-recipient-has-partner': {
      routes: ['waffle'],
    },
    'care-recipient-partner-date-of-birth': {
      routes: ['waffle'],
    },
    'care-recipient-savings-held-in-bank-accounts': {
      routes: ['typed', 'document'],
    },
    'care-recipient-joint-weekly-income': {
      routes: ['typed', 'document'],
    },
    'care-recipient-weekly-income': {
      routes: ['typed', 'document'],
    },
  };

// ── The ledger: grouped, ordered, and rated for reliability ──────────────────

// How much a brain-dump can be trusted to answer a question.
//
// This is a PRIOR, not a measurement. It is authored design judgement with the
// reason stated per row, so each one can be argued with and, more to the point,
// tested. The divergence plan already has a dev spike for inference quality with
// no defined target; this table is what that spike should measure — nineteen
// falsifiable claims instead of "will the AI work".
export type Reliability = 'firm' | 'hedged' | 'never';

export const RELIABILITY_META: Record<
  Reliability,
  { label: string; tone: 'lime' | 'sun' | 'stop'; meaning: string }
> = {
  firm: {
    label: 'Firm',
    tone: 'lime',
    meaning: 'Said unprompted, and a mishearing still lands in the right band.',
  },
  hedged: {
    label: 'Hedged',
    tone: 'sun',
    meaning:
      'Gives a direction, not a value. Propose it and make them confirm.',
  },
  never: {
    label: 'Never infer',
    tone: 'stop',
    meaning:
      'Asked outright. Nobody says it out loud, or being wrong costs too much.',
  },
};

// Does the answer go off? Decides what needs reconfirming later, and it maps to
// a field production already has: Fact.ExpiresAt.
export type Decay = 'stable' | 'changes';

// How the calculator is grouped and ordered today, transcribed from
// api/domain/care_form/seed/category-json/8-eligibility-calculator.json. Five
// subcategories, seventeen questions, asked in this order.
//
// `required` is the real isRequired flag from the seed JSON. Exactly one question
// carries it. That is the single most load-bearing fact about this whole design:
// because nothing else is required, the calculator degrades to "need to know
// more" instead of blocking, which is the only reason an answer is allowed to
// arrive late, out of order, or never. A gated form could not be fed by a note.
export const TODAY = {
  title: 'How it is grouped today',
  // Counts are computed from the groups below, per the no-figures-in-copy rule.
  subtitle: (groups: number, questions: number) =>
    `${groups} subcategories, ${questions} questions, in this order. Straight out of the category seed.`,
  groups: [
    {
      title: 'Basic information',
      questions: [
        { label: 'Location', required: false },
        { label: 'Date of birth', required: false },
      ],
    },
    {
      title: 'Partner & household',
      questions: [
        { label: 'Relationship status', required: false },
        { label: 'Partner date of birth', required: false },
        { label: 'Joint weekly income', required: false },
        { label: 'Personal weekly income', required: false },
      ],
    },
    {
      title: 'Medical',
      questions: [
        { label: 'Health condition or disability', required: false },
        { label: 'What they need support with', required: true },
        { label: 'Condition minimum duration', required: false },
        { label: 'Condition expected duration', required: false },
        { label: 'Help throughout the day or night', required: false },
        { label: 'Home adaptations required', required: false },
      ],
    },
    {
      title: 'Financial',
      questions: [{ label: 'Savings', required: false }],
    },
    {
      title: 'Care needs',
      questions: [
        { label: 'Current residence', required: false },
        { label: 'Personal care required', required: false },
        { label: 'Nursing care required', required: false },
      ],
    },
  ],
};

// The groups, in the order the route asks them. Group order is the design: open
// with what someone will tell you unprompted, and leave the things they have to
// go and look up until they can see why it is worth it.
export const GROUPS: { id: string; title: string; intent: string }[] = [
  {
    id: 'who',
    title: 'Who you are caring for',
    intent:
      'Comes out of the first sentence anyone says, and it tells us who they are talking about.',
  },
  {
    id: 'care',
    title: 'What the care is like',
    intent:
      'The biggest group, and most of what a carer will tell you. Mostly hedged, because people describe a day rather than pick a category.',
  },
  {
    id: 'already',
    title: 'What they already get',
    intent: 'One question. An award letter answers it outright.',
  },
  {
    id: 'carer',
    title: 'About the carer',
    intent:
      'These are about the person talking, not the person they care for. Nothing here is asked today.',
  },
  {
    id: 'money',
    title: 'Money',
    intent:
      'Asked last, because these send someone off to find a piece of paper. By now they can see it is worth it.',
  },
];

// Per question: which group it belongs to, how far a note can be trusted with it,
// and whether the answer goes stale. `basis` is the reason for the rating, so a
// row can be disagreed with specifically rather than in general.
export const LEDGER: Record<
  string,
  { group: string; reliability: Reliability; decay: Decay; basis: string }
> = {
  'proto-carer-present': {
    group: 'who',
    reliability: 'firm',
    decay: 'changes',
    basis:
      'They are the one talking, and they describe what they do. The risk is not accuracy, it is filing it against the wrong person.',
  },
  'care-recipient-has-partner': {
    group: 'who',
    reliability: 'firm',
    decay: 'stable',
    basis: '"It is just me" and "her husband" both settle it in one clause.',
  },
  'care-recipient-date-of-birth': {
    group: 'who',
    reliability: 'firm',
    decay: 'stable',
    basis:
      'People state an age early and unprompted, and the question only wants which side of 60 and 66 they are.',
  },
  'care-recipient-partner-date-of-birth': {
    group: 'who',
    reliability: 'hedged',
    decay: 'stable',
    basis:
      'Mentioned less often than her own age, and easily attached to the wrong person in a sentence about both of them.',
  },
  'care-recipient-location': {
    group: 'who',
    reliability: 'hedged',
    decay: 'stable',
    basis:
      'People name a town, not a nation. Mapping a town is reliable; getting a town mentioned at all is not.',
  },
  'care-recipient-is-disabled': {
    group: 'care',
    reliability: 'firm',
    decay: 'changes',
    basis:
      'Anyone describing why they are here has already answered this. Still proposed rather than assumed.',
  },
  'care-recipient-needs-personal-care': {
    group: 'care',
    reliability: 'firm',
    decay: 'changes',
    basis:
      'Washing, dressing and eating get described concretely, because they are the day.',
  },
  'care-recipient-in-nursing-home': {
    group: 'care',
    reliability: 'firm',
    decay: 'changes',
    basis: 'People say where the person lives in the first few sentences.',
  },
  'care-recipient-needs-help-time-of-day': {
    group: 'care',
    reliability: 'hedged',
    decay: 'changes',
    basis:
      'The day is described and the night is implied. "She gets up in the night" is not the same as needing help then, and the difference is a rate of Attendance Allowance.',
  },
  'care-recipient-condition-duration': {
    group: 'care',
    reliability: 'hedged',
    decay: 'stable',
    basis:
      '"A couple of years since the stroke" is a date to us and a shrug to them. Close enough for a six-month threshold, not for anything finer.',
  },
  'care-recipient-condition-expected': {
    group: 'care',
    reliability: 'hedged',
    decay: 'stable',
    basis:
      'A prognosis, and often not known yet. Only asked when it has been under six months.',
  },
  'care-recipient-home-requires-adaptations': {
    group: 'care',
    reliability: 'hedged',
    decay: 'changes',
    basis:
      'Struggling with the stairs is evidence the home might need adapting. It is not a statement that it does.',
  },
  'care-recipient-needs-nursing-care': {
    group: 'care',
    reliability: 'never',
    decay: 'changes',
    basis:
      'Nursing care against personal care is a funding distinction, not a lay one. Nobody should be asked to draw it by accident.',
  },
  'care-recipient-disability-type': {
    group: 'care',
    reliability: 'hedged',
    decay: 'stable',
    basis:
      'A walking stick gets mentioned; whether that is a walking disability for a bus pass does not.',
  },
  'care-recipient-terminally-ill': {
    group: 'care',
    reliability: 'never',
    decay: 'stable',
    basis:
      'Fast-tracks a claim, and being wrong in either direction is unforgivable. Asked outright, gently, or not at all.',
  },
  'proto-existing-awards': {
    group: 'already',
    reliability: 'hedged',
    decay: 'changes',
    basis:
      'The benefit gets named and the rate does not. "The lower one I think" is the whole problem in five words.',
  },
  'proto-pip-parts': {
    group: 'already',
    reliability: 'never',
    decay: 'changes',
    basis:
      'Which part they get is on the award letter and almost nowhere else. Asked only of somebody who has it.',
  },
  'proto-dla-parts': {
    group: 'already',
    reliability: 'never',
    decay: 'changes',
    basis:
      'Which part they get is on the award letter and almost nowhere else. Asked only of somebody who has it.',
  },
  'proto-adp-parts': {
    group: 'already',
    reliability: 'never',
    decay: 'changes',
    basis:
      'Which part they get is on the award letter and almost nowhere else. Asked only of somebody who has it.',
  },
  'proto-carer-hours-35-plus': {
    group: 'carer',
    reliability: 'hedged',
    decay: 'changes',
    basis:
      '"I am there most days" is not thirty-five hours, and the threshold is exact.',
  },
  'proto-carer-earnings-below-204': {
    group: 'carer',
    reliability: 'never',
    decay: 'changes',
    basis:
      "Nobody volunteers their weekly pay. It is also the single answer standing between this carer and Carer's Allowance.",
  },
  'care-recipient-savings-held-in-bank-accounts': {
    group: 'money',
    reliability: 'never',
    decay: 'changes',
    basis:
      'Not said out loud, not guessable, and wrong by one band moves a means test.',
  },
  'care-recipient-weekly-income': {
    group: 'money',
    reliability: 'never',
    decay: 'changes',
    basis:
      'Same as savings. Pension plus benefits is arithmetic most people have never done.',
  },
  'care-recipient-joint-weekly-income': {
    group: 'money',
    reliability: 'never',
    decay: 'changes',
    basis: 'As above, and now for two people.',
  },
};

// Which of today's five subcategories each question sits in, so the ledger can
// show the regroup as a reshuffle rather than a fresh invention. Transcribed from
// the same category seed as TODAY. Absent means it is not asked today at all.
export const TODAY_SECTION: Record<string, string> = {
  'care-recipient-location': 'Basic information',
  'care-recipient-date-of-birth': 'Basic information',
  'care-recipient-has-partner': 'Partner & household',
  'care-recipient-partner-date-of-birth': 'Partner & household',
  'care-recipient-joint-weekly-income': 'Partner & household',
  'care-recipient-weekly-income': 'Partner & household',
  'care-recipient-is-disabled': 'Medical',
  'care-recipient-condition-duration': 'Medical',
  'care-recipient-condition-expected': 'Medical',
  'care-recipient-disability-type': 'Medical',
  'care-recipient-terminally-ill': 'Medical',
  'care-recipient-home-requires-adaptations': 'Medical',
  'care-recipient-needs-help-time-of-day': 'Medical',
  'care-recipient-savings-held-in-bank-accounts': 'Financial',
  'care-recipient-in-nursing-home': 'Care needs',
  'care-recipient-needs-personal-care': 'Care needs',
  'care-recipient-needs-nursing-care': 'Care needs',
};

// Short labels, so a list of nineteen questions can be read at a glance. The
// full prompts live in the screener and are shown on the frames themselves.
export const SHORT_LABEL: Record<string, string> = {
  'care-recipient-location': 'Which nation',
  'care-recipient-date-of-birth': 'Her age',
  'care-recipient-needs-help-time-of-day': 'Help, day or night',
  'care-recipient-is-disabled': 'Disability or condition',
  'care-recipient-condition-duration': 'Had it six months',
  'care-recipient-condition-expected': 'Will last a year',
  'care-recipient-disability-type': 'Bus pass disability',
  'care-recipient-terminally-ill': 'Terminal illness',
  'care-recipient-home-requires-adaptations': 'Home needs adapting',
  'care-recipient-in-nursing-home': 'In a care home',
  'care-recipient-needs-personal-care': 'Needs personal care',
  'care-recipient-needs-nursing-care': 'Needs nursing care',
  'proto-existing-awards': 'What she already gets',
  'proto-pip-parts': 'Which part of PIP',
  'proto-dla-parts': 'Which parts of DLA',
  'proto-adp-parts': 'Which part of ADP',
  'proto-carer-present': 'Someone cares for her',
  'proto-carer-hours-35-plus': 'Carer does 35 hours',
  'proto-carer-earnings-below-204': 'Carer earns under £204',
  'care-recipient-has-partner': 'Partner or spouse',
  'care-recipient-partner-date-of-birth': 'Partner also 66 or over',
  'care-recipient-savings-held-in-bank-accounts': 'Her savings',
  'care-recipient-joint-weekly-income': 'Their joint income',
  'care-recipient-weekly-income': 'Her weekly income',
};

// Worked examples, answered in full, so the frames can run the engine rather
// than assert. A1 uses the first of these for its load-bearing test: drop a
// question from a complete set of answers and see what stops resolving.
//
// Two of them, deliberately unalike, because that test gives a different result
// for each — which is worth remembering when reading a single column of it.
// Every value is a real option id from the screener.
export const PEOPLE: { label: string; note: string; answers: Answers }[] = [
  {
    label: 'Margaret, 84',
    note: 'England, lives alone, needs help day and night, already gets Attendance Allowance. Her daughter is the carer.',
    answers: {
      'care-recipient-location': 'care-recipient-location-england',
      'care-recipient-date-of-birth': 'age-66-plus',
      'care-recipient-needs-help-time-of-day':
        'care-recipient-needs-help-during-both',
      'care-recipient-is-disabled': true,
      'care-recipient-condition-duration': 'duration-6-months',
      'care-recipient-terminally-ill': false,
      'care-recipient-home-requires-adaptations': true,
      'care-recipient-in-nursing-home': false,
      'proto-existing-awards': ['award-attendance-allowance'],
      'proto-carer-present': true,
      'proto-carer-hours-35-plus': true,
      'proto-carer-earnings-below-204': true,
      'care-recipient-has-partner': false,
      'care-recipient-savings-held-in-bank-accounts': 'savings-below-16k',
      'care-recipient-weekly-income': 'income-below-238',
    },
  },
  {
    label: 'Rab, 58',
    note: 'Scotland, lives with his wife, needs personal and nursing care at home, gets nothing yet.',
    answers: {
      'care-recipient-location': 'care-recipient-location-scotland',
      'care-recipient-date-of-birth': 'age-18-59',
      'care-recipient-needs-help-time-of-day':
        'care-recipient-needs-help-during-day',
      'care-recipient-is-disabled': true,
      'care-recipient-condition-duration': 'duration-under-6-months',
      'care-recipient-condition-expected': 'expected-12-months',
      'care-recipient-terminally-ill': false,
      'care-recipient-home-requires-adaptations': true,
      'care-recipient-needs-personal-care': true,
      'care-recipient-needs-nursing-care': true,
      'proto-existing-awards': ['award-none'],
      'proto-carer-present': true,
      'proto-carer-hours-35-plus': true,
      'proto-carer-earnings-below-204': true,
      'care-recipient-has-partner': true,
      'care-recipient-partner-date-of-birth': false,
      'care-recipient-savings-held-in-bank-accounts': 'savings-below-16k',
      'care-recipient-joint-weekly-income': 'joint-below-364',
    },
  },
];

// The ledger. Regrouped by who can answer it and how, rather than by topic, and
// reordered so the route opens with what people volunteer. The unlocks line is
// computed by dropping each question from Margaret's fully answered set.
export const ROUTING = {
  // Deliberately parallel to TODAY.title, since the two frames sit next to each
  // other and the pair is the argument.
  title: 'How it could be grouped instead',
  subtitle:
    'Five groups, ordered so the route opens with what people volunteer and leaves what they have to look up until last.',
  unlocksPrefix: 'Load-bearing for',
  gatePrefix: 'Only asked if',
  notAskedToday: 'Not asked today',
  todayPrefix: 'Today:',
  reliabilityLegend: 'HOW FAR THE CONVERSATION CAN BE TRUSTED',
  channelLegend: 'WHERE THE ANSWER COMES FROM',
  footnote:
    'Ask is only marked on the two questions a conversation must never answer. Everything else can still be asked if nothing else works, so it is not worth marking. The reliability ratings are my guesses, not test results.',
};

// ── B. Tap: what exists today ────────────────────────────────────────────────

export const TAP = {
  progress: (n: number, of: number) => `Question ${n} of about ${of}`,
  pruned: {
    title: 'Three questions just disappeared',
    body: 'Margaret is 84, so we stopped asking anything that only applies under 66.',
  },
  results: {
    title: 'Worth pursuing',
    subtitle: 'Six answers in. Nothing here is a decision.',
    estimate: 'estimated a year',
    moreInfo: 'Need to know more',
  },
};

// ── C. The second visit, when we do hold something ───────────────────────────

export const PROPOSAL = {
  badge: 'Already answered',
  invite: 'Is this still right?',
  confirm: 'Yes, that is right',
  change: 'Change it',
  sources: {
    held: 'You told us this in March, on the care profile.',
    document: 'From the council tax bill you sent us.',
    derived: 'Worked out from her date of birth.',
  },
  batch: {
    title: 'What we already know about Margaret',
    subtitle:
      'Four of the six answers are already here. Check them and we will only ask you the two that are missing.',
    cta: 'These are right',
    edit: 'Something has changed',
  },
  changed: {
    title: 'What changed?',
    body: 'We will use the new answer and keep the old one on the record.',
  },
};

// ── D. The brain-dump ────────────────────────────────────────────────────────

export const WAFFLE = {
  // The front door, not an alternative to one. Deliberately a situation rather
  // than a question: who they are caring for, and what it is like.
  invite: {
    title: 'Tell us about your care situation',
    body: 'Who are you caring for, and how are they doing? What they struggle with, who helps, anything they already get. Talk or type, whichever is easier. We will pull out what we can and check every bit of it with you.',
    type: 'Or type it instead',
  },
  recording: {
    title: 'Listening',
    hint: 'Take your time. Tap when you are done.',
    stop: 'Done',
    elapsed: '1:24',
  },
  // No interstitial. Submitting drops you straight onto the next question, and
  // this quiet strip is the only acknowledgement that anything is happening.
  meanwhile: {
    banner: 'Reading your note in the background',
    body: 'Nothing to wait for. Here is one we could never have got from it anyway.',
  },
  landed: {
    title: 'We read your note',
    body: 'There are some answers in it. Now is a good time to check them.',
    cta: 'Check them',
  },
  review: {
    title: 'From what you told us',
    subtitle: 'Five answers. Nothing is saved until you say so.',
    confirmAll: 'All correct',
    saidPrefix: 'You said',
  },
  done: {
    title: 'One more thing worth pursuing',
    body: 'Your note answered six questions. It moved the list by one, and it did not move the money at all.',
  },
  // The note itself, as the user would have said it. Every proposed answer
  // below has to be traceable to a clause in here.
  transcript:
    'She is 84 and she has been struggling since the stroke, that was about two years ago now. I am there most days, I do her breakfast and her tablets, and I am back in the evening. She has had a few falls at night getting to the loo. She gets the attendance allowance already, the lower one I think. It is just me, my dad passed in 2019.',
  // The six answers the transcript genuinely supports. These are the ones the
  // frames actually feed to the engine, so what the note is worth is measured
  // rather than asserted.
  proposals: [
    {
      question: 'Does Margaret have a disability or long-term condition?',
      value: 'Yes',
      said: 'she has been struggling since the stroke',
    },
    {
      question: 'Has she had it for six months or more?',
      value: 'Yes',
      said: 'that was about two years ago now',
    },
    {
      question: 'Does Margaret already get any of these?',
      value: 'Attendance Allowance',
      said: 'She gets the attendance allowance already',
    },
    {
      question: 'Does Margaret have a partner or spouse?',
      value: 'No',
      said: 'It is just me, my dad passed in 2019',
    },
    {
      question: 'Are you caring for Margaret unpaid?',
      value: 'Yes',
      said: 'I am there most days, I do her breakfast and her tablets',
    },
    {
      question: 'Is that 35 hours a week or more?',
      value: 'Yes',
      said: 'I am there most days, and I am back in the evening',
    },
  ],
  // What the note did not settle, so it stays a gap rather than a guess.
  gaps: [
    'Which rate she gets. She said "the lower one I think", and the rate changes what else opens up.',
    'Her savings and her weekly income. Neither was mentioned, and neither should ever be inferred.',
    "What the carer earns. It is what decides Carer's Allowance, and nobody says their weekly pay in passing.",
  ],
};

// The three answer sets the journey is made of, so what each mode is worth can
// be measured by running them rather than described. `note` is exactly the six
// proposals above; `typed` is the three questions in the bottom routing band.
export const JOURNEY: { taps: Answers; note: Answers; typed: Answers } = {
  taps: {
    'care-recipient-location': 'care-recipient-location-england',
    'care-recipient-date-of-birth': 'age-66-plus',
    'care-recipient-needs-help-time-of-day':
      'care-recipient-needs-help-during-both',
  },
  note: {
    'care-recipient-is-disabled': true,
    'care-recipient-condition-duration': 'duration-6-months',
    'proto-existing-awards': ['award-attendance-allowance'],
    'care-recipient-has-partner': false,
    'proto-carer-present': true,
    'proto-carer-hours-35-plus': true,
  },
  typed: {
    'care-recipient-savings-held-in-bank-accounts': 'savings-below-16k',
    'care-recipient-weekly-income': 'income-below-238',
    'proto-carer-earnings-below-204': true,
  },
};

// Labels for the before/after panel. Again, no figures.
export const DELTA = {
  before: 'BEFORE',
  after: 'AFTER',
  appeared: 'New on the list',
  nothingNew: 'Nothing new on the list',
  noMoney: 'No change to the estimated money',
  moreMoney: 'more a year, estimated',
};

// ── E. Documents ─────────────────────────────────────────────────────────────

export const DOCUMENTS = {
  ask: {
    title: 'Do you have her award letter?',
    body: 'A photo of the first page is fine. We only want to know what she already gets, so we do not ask you to remember it.',
    photo: 'Take a photo',
    upload: 'Choose a file',
    skip: 'I will answer it myself',
  },
  reading: {
    title: 'Reading your letter',
    body: 'A minute or two. You can carry on.',
    filename: 'award-letter.jpg',
  },
  found: {
    title: 'One thing off that letter',
    body: 'We only looked for what she already gets. We did not read the rest.',
    row: {
      question: 'Does Margaret already get any of these?',
      value: 'Attendance Allowance, lower rate',
      source: 'Attendance Allowance, £73.90 a week',
    },
    confirm: 'That is right',
    reject: 'That is not right',
  },
  unreadable: {
    title: 'We cannot read this one',
    body: 'The text is too soft to be sure, and we will not guess at a benefit. Try again in better light, or just tell us instead.',
    retake: 'Take another photo',
    instead: 'Tell us instead',
  },
  wrongDoc: {
    title: 'Nothing we can use here',
    body: 'This looks like an appointment letter. We were after the award letter from the DWP, the one with the weekly amount on it.',
    retake: 'Try a different one',
    instead: 'Tell us instead',
  },
};

// ── F. Typed answers ─────────────────────────────────────────────────────────

export const PRECISE = {
  // The question itself comes from the screener, so only the framing lives here.
  savings: {
    body: 'A rough figure is fine. We only need to know which side of £16,000 and £23,250 she is on.',
    unsure: 'I need to check',
  },
  worth: {
    title: 'What three numbers are worth',
    body: 'Her savings, her weekly income, and what her daughter earns. Nobody knows any of them offhand, and every one of them is written on a piece of paper she already has.',
  },
  unsure: {
    title: 'We will keep your place',
    body: 'Go and look. Everything else you have told us is saved, and this is the only one left.',
    cta: 'Done for now',
  },
  escape: {
    title: 'Or answer it yourself',
    body: 'We are still reading your note. If you would rather not wait, answer it here and we will drop it from the note when it comes back.',
    cta: 'Answer it here',
  },
};

// ── G. When it goes wrong ────────────────────────────────────────────────────

export const RECONCILE = {
  conflict: {
    title: 'These two do not match',
    body: 'In March you told us Margaret was under £238 a week. Your note sounds more like £300. Which is right now?',
    options: ['Under £238 a week', 'About £300 a week'],
    keep: 'It has changed since March',
  },
  subject: {
    title: 'Is this about you or about Margaret?',
    body: 'You said "I am there most days". We think that is about you, not her. It matters, because it is what opens Carer\'s Allowance.',
    options: ['That is about me', 'That is about Margaret'],
  },
  stale: {
    title: 'Still true?',
    body: 'You told us these in March. Falls and care hours are the two that move.',
    rows: [
      { question: 'Help through the day or night', value: 'In the day' },
      { question: 'Falls in the last month', value: 'None' },
    ],
    yes: 'Still true',
    no: 'This has changed',
  },
  retired: {
    title: 'We did not need that one',
    body: 'Your note answered the six-month question, but Margaret already qualifies on her age. We have kept the answer and stopped asking.',
    cta: 'Fine',
  },
};

// ── H. The seam ──────────────────────────────────────────────────────────────

export const SEAM = {
  block: {
    title: 'Reading your note',
    body: 'This takes a few minutes. Hang on and we will show you what we found.',
    progress: 'About two minutes left',
  },
  carryOn: {
    banner: 'Still reading your note',
    body: 'We cannot get this from a voice note anyway.',
  },
  notify: {
    title: 'That is us for now',
    body: 'We will send you a message when we have read your note. Nothing else to do.',
    cta: 'Close',
    push: {
      title: 'We found five answers in your note',
      body: 'Have a look and tell us if we got them right.',
    },
  },
};
